import React, { useState } from 'react';
import * as PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import InfoChampsObligatoire from 'components/form/infoObligatoire/InfoChampsObligatoire';
import {
    formaterOptions,
    getMessageErreurChampManquant,
    getPatternHookFormValidation,
} from 'utils/utils';
import moment from 'moment';
import { Col, InputControl, Row, SelectControl } from '@si-armes/dsfr-react';
import {
    REGEXP_DATE_AVEC_ANNEE,
    REGEXP_DATE_AVEC_MOIS_JOUR_INCONNU,
    REGEXP_EMAIL,
    REGEXP_NOM_PRENOM,
} from 'constants/contraintes.constants';
import { MESSAGE_DATE_INVALIDE } from 'constants/messagePersonalise.constants';
import {
    getDateFormatWithSlash,
    getTelFormatWithoutSpace,
} from 'components/Commun/Form/maskFunctions';
import SelectPays from 'components/form/referentiel/select/SelectPays';
import SelectCommune from 'components/form/referentiel/select/SelectCommune';
import InputFiles from 'components/Commun/Form/InputFiles';

import InformationLicencie from './licence/InformationLicencie';
import InformationChasseur from './licence/InformationChasseur';

const CompteIdentiteChamps = ({
    featureFlipping,
    motifs,
    hasEmailPro = false,
    hasTelephone = false,
    titre,
}) => {
    const { register, getValues, formState, setValue, watch } = useFormContext();

    // SelectCommune params
    const [valueRecherche, setValueRecherche] = useState(getValues('lieuNaissance') ?? '');

    const typePieceIdentite = [
        { key: 'CNI', value: 2, libelle: "Carte nationale d'identité" },
        { key: 'passeport', value: 33, libelle: 'Passeport' },
        { key: 'titreSejour', value: 34, libelle: 'Titre de séjour' },
        { key: 'pieceIdentiteEtrangere', value: 35, libelle: 'Pièce identité étrangère' },
    ];
    const choixSexe = [
        { key: 'F', value: '2', libelle: 'Féminin' },
        { key: 'M', value: '1', libelle: 'Masculin' },
    ];

    const validateDate = (dateValue) => {
        const dateSplit = dateValue.split('/');
        const dateExistante = new Date(dateSplit[2], dateSplit[1] - 1, dateSplit[0]);
        if (
            (dateSplit &&
                moment(`${dateSplit[2]}-${dateSplit[1]}-${dateSplit[0]}`).diff(moment()) > 0) ||
            (dateSplit[1] !== '00' &&
                dateSplit[0] !== '00' &&
                (dateExistante.getFullYear() !== parseInt(dateSplit[2], 10) ||
                    dateExistante.getMonth() + 1 !== parseInt(dateSplit[1], 10) ||
                    dateExistante.getDate() !== parseInt(dateSplit[0], 10)))
        ) {
            return MESSAGE_DATE_INVALIDE;
        }
        return null;
    };

    const validatePrenomUsuel = (valuePrenomUsuel) => {
        if (
            valuePrenomUsuel &&
            valuePrenomUsuel !== getValues('prenomUn') &&
            valuePrenomUsuel !== getValues('prenomDeux') &&
            valuePrenomUsuel !== getValues('prenomTrois') &&
            valuePrenomUsuel !== getValues('prenomQuatre')
        ) {
            return 'Le prénom usuel doit correspondre à un des autres prénoms';
        }
        return null;
    };

    const validateMajorite = (valueDateNaissance) => {
        if (valueDateNaissance.length === 10) {
            const ageMajorite = 18;
            const dateNaissanceSplit = valueDateNaissance.split('/');
            const today = new Date();
            const thisYear = today.getFullYear();
            const birthDay = dateNaissanceSplit[0];
            const birthMonth = dateNaissanceSplit[1];
            const birthYear = dateNaissanceSplit[2];
            const valideAge = new RegExp(REGEXP_DATE_AVEC_ANNEE);

            if (valideAge.test(birthYear)) {
                if (
                    ((birthDay === '00' || birthMonth === '00') &&
                        birthYear > thisYear - ageMajorite) ||
                    moment().diff(moment(birthYear + birthMonth + birthDay, 'YYYYMMDD'), 'years') <
                        ageMajorite
                ) {
                    return "L'Espace Détenteurs n'est pas encore ouvert aux détenteurs mineurs.";
                }
            }
        }

        return null;
    };

    const numeroNonFrancais = (telephone) => {
        if (watch(telephone) && watch(telephone).length > 10) {
            return {
                message:
                    'Le format du numéro de téléphone ne correspond pas à un numéro de téléphone français',
            };
        }
        return null;
    };

    return (
        <>
            <Row>
                <Col col={12}>
                    <InfoChampsObligatoire />
                </Col>
            </Row>

            {motifs && motifs.isChasseur && <InformationChasseur />}
            {motifs && motifs.isLicencie && (
                <InformationLicencie featureFlipping={featureFlipping} />
            )}
            <Row className="margin-top-lg">
                <Col col={12}>
                    <h3>{titre}</h3>
                </Col>
            </Row>
            <Row gutters verticalAlign="bottom">
                <Col col={12} lg={6}>
                    <SelectControl
                        id="sexe"
                        name="Sexe *"
                        label="Sexe *"
                        options={formaterOptions(choixSexe)}
                        required
                        state={formState.errors?.sexe ? 'error' : null}
                        message={formState.errors?.sexe?.message}
                        {...register('sexe', {
                            required: getMessageErreurChampManquant('sexe'),
                        })}
                        placeholder="-- Choisir une option --"
                    />
                </Col>
                <Col col={12} lg={6}>
                    <InputControl
                        autocomplete="family-name"
                        id="nom"
                        label="Nom *"
                        required
                        uppercase
                        {...register('nom', {
                            required: getMessageErreurChampManquant('nom'),
                            pattern: getPatternHookFormValidation(REGEXP_NOM_PRENOM, 'nom'),
                        })}
                        state={formState.errors?.nom ? 'error' : null}
                        message={formState.errors?.nom?.message}
                    />
                </Col>
            </Row>

            <Row gutters verticalAlign="bottom">
                <Col col={12} lg={6}>
                    <InputControl
                        autocomplete="family-name"
                        id="nomUsage"
                        label="Nom d'usage"
                        hintText="Nom de famille utilisé dans la vie courante, à renseigner s'il ne s'agit pas du nom de naissance."
                        uppercase
                        {...register('nomUsage', {
                            pattern: getPatternHookFormValidation(REGEXP_NOM_PRENOM, "nom d'usage"),
                        })}
                        state={formState.errors?.nomUsage ? 'error' : null}
                        message={formState.errors?.nomUsage?.message}
                    />
                </Col>
                <Col col={12} lg={6}>
                    <InputControl
                        autocomplete="given-name"
                        id="prenomUn"
                        label="Premier prénom *"
                        required
                        uppercase
                        {...register('prenomUn', {
                            required: getMessageErreurChampManquant('premier prénom'),
                            pattern: getPatternHookFormValidation(
                                REGEXP_NOM_PRENOM,
                                'premier prénom',
                            ),
                        })}
                        state={formState.errors?.prenomUn ? 'error' : null}
                        message={formState.errors?.prenomUn?.message}
                    />
                </Col>
            </Row>
            <Row gutters verticalAlign="bottom">
                <Col col={12} lg={6}>
                    <InputControl
                        autocomplete="additional-name"
                        id="prenomDeux"
                        label="Deuxième prénom"
                        disabled={!getValues('prenomUn')}
                        {...register('prenomDeux', {
                            pattern: getPatternHookFormValidation(
                                REGEXP_NOM_PRENOM,
                                'deuxième prénom',
                            ),
                        })}
                        uppercase
                        state={formState.errors?.prenomDeux ? 'error' : null}
                        message={formState.errors?.prenomDeux?.message}
                    />
                </Col>
                <Col col={12} lg={6}>
                    <InputControl
                        autocomplete="additional-name"
                        id="prenomTrois"
                        label="Troisième prénom"
                        disabled={!getValues('prenomUn') || !getValues('prenomDeux')}
                        {...register('prenomTrois', {
                            pattern: getPatternHookFormValidation(
                                REGEXP_NOM_PRENOM,
                                'troisième prénom',
                            ),
                        })}
                        uppercase
                        state={formState.errors?.prenomTrois ? 'error' : null}
                        message={formState.errors?.prenomTrois?.message}
                    />
                </Col>
            </Row>
            <Row gutters verticalAlign="bottom">
                <Col col={12} lg={6}>
                    <InputControl
                        autocomplete="additional-name"
                        id="prenomQuatre"
                        label="Quatrième prénom"
                        disabled={
                            !getValues('prenomUn') ||
                            !getValues('prenomDeux') ||
                            !getValues('prenomTrois')
                        }
                        {...register('prenomQuatre', {
                            pattern: getPatternHookFormValidation(
                                REGEXP_NOM_PRENOM,
                                'quatrième prénom',
                            ),
                        })}
                        uppercase
                        state={formState.errors?.prenomQuatre ? 'error' : null}
                        message={formState.errors?.prenomQuatre?.message}
                    />
                </Col>
                <Col col={12} lg={6}>
                    <InputControl
                        autocomplete="given-name"
                        id="prenomUsuel"
                        label="Prénom usuel"
                        hintText="Prénom utilisé dans la vie courante, il doit être identique à l'un des prénoms renseignés dans le formulaire."
                        {...register('prenomUsuel', {
                            pattern: getPatternHookFormValidation(
                                REGEXP_NOM_PRENOM,
                                'prénom usuel',
                            ),
                            validate: (value) => validatePrenomUsuel(value),
                        })}
                        uppercase
                        state={formState.errors?.prenomUsuel ? 'error' : null}
                        message={formState.errors?.prenomUsuel?.message}
                    />
                </Col>
            </Row>
            <Row gutters verticalAlign="bottom">
                <Col col={12} lg={6}>
                    <InputControl
                        autocomplete="bday"
                        id="dateNaissance"
                        label="Date de naissance *"
                        required
                        hintText="Exemple : 27/05/2001"
                        {...register('dateNaissance', {
                            required: getMessageErreurChampManquant('Date de naissance'),
                            pattern: getPatternHookFormValidation(
                                REGEXP_DATE_AVEC_MOIS_JOUR_INCONNU,
                                '',
                                MESSAGE_DATE_INVALIDE,
                            ),
                            validate: {
                                validateDate: (value) => validateDate(value),
                                validateMajorite: (value) => validateMajorite(value),
                            },
                            onChange: () => {
                                setValueRecherche('');
                                setValue('lieuNaissance', '');
                            },
                        })}
                        state={formState.errors?.dateNaissance ? 'error' : null}
                        message={formState.errors?.dateNaissance?.message}
                        maskFonction={getDateFormatWithSlash}
                    />
                </Col>
                <Col col={12} lg={6}>
                    <SelectPays
                        id="paysNaissance"
                        name="paysNaissance"
                        label="Pays de naissance *"
                        required
                        register={register('paysNaissance', {
                            onChange: () => {
                                setValueRecherche('');
                                setValue('lieuNaissance', '');
                            },
                            required: getMessageErreurChampManquant('pays de Naissance'),
                        })}
                        placeholder="-- Choisir une option --"
                    />
                </Col>
            </Row>
            <Row gutters verticalAlign="bottom">
                <Col col={12} lg={6}>
                    <SelectCommune
                        name="communneNaissance"
                        label="Lieu de naissance"
                        valueRecherche={valueRecherche}
                        setValueRecherche={setValueRecherche}
                    />
                </Col>
            </Row>
            <Row gutters verticalAlign="bottom">
                <Col col={12} lg={6}>
                    <SelectPays
                        id="nationalite"
                        name="nationalite"
                        label="Nationalité *"
                        required
                        register={register('nationalite', {
                            required: getMessageErreurChampManquant('nationalité'),
                        })}
                        placeholder="-- Choisir une option --"
                    />
                </Col>
                {motifs && !motifs.isNonLicencie && (
                    <Col col={12} lg={6}>
                        <InputControl
                            id="numeroSia"
                            label="Si vous possédez déjà un numéro SIA, veuillez le renseigner ci-dessous"
                            uppercase
                            {...register('numeroSia', {
                                setValueAs: (v) => (v ? v.toUpperCase() : null),
                            })}
                        />
                    </Col>
                )}

                {hasEmailPro && (
                    <Col col={12} lg={6}>
                        <InputControl
                            id="email"
                            autocomplete="email"
                            label="E-mail professionnel *"
                            type="text"
                            required
                            {...register('email', {
                                required: 'Le champ est obligatoire',
                                pattern: {
                                    value: REGEXP_EMAIL,
                                    message: "Le format de l'adresse courriel n'est pas valide",
                                },
                            })}
                            state={formState.errors?.email ? 'error' : null}
                            message={formState.errors?.email?.message}
                        />
                    </Col>
                )}
            </Row>

            {hasTelephone && (
                <Row gutters verticalAlign="bottom">
                    <Col col={12} lg={6}>
                        <InputControl
                            autocomplete="tel-national"
                            id="telephonePrincipal"
                            label="Téléphone *"
                            hintText="Exemple: 0612345678"
                            required
                            type="text"
                            maxLength="10"
                            {...register('telephonePrincipal', {
                                required: getMessageErreurChampManquant('Téléphone'),
                            })}
                            state={formState.errors?.telephonePrincipal ? 'error' : null}
                            message={formState.errors?.telephonePrincipal?.message}
                            warning={numeroNonFrancais('telephonePrincipal')}
                            maskFonction={getTelFormatWithoutSpace}
                        />
                    </Col>
                    <Col col={12} lg={6}>
                        <InputControl
                            autocomplete="tel-national"
                            id="telephoneSecondaire"
                            label="Téléphone"
                            hintText="Exemple: 0198765432"
                            type="text"
                            maxLength="10"
                            {...register('telephoneSecondaire')}
                            state={formState.errors?.telephoneSecondaire ? 'error' : null}
                            message={formState.errors?.telephoneSecondaire?.message}
                            warning={numeroNonFrancais('telephoneSecondaire')}
                            maskFonction={getTelFormatWithoutSpace}
                        />
                    </Col>
                </Row>
            )}

            <Row gutters horizontalAlign="center" verticalAlign="bottom">
                <Col col={12} lg={6}>
                    <div className="sia-input-alignementSansTexteAide">
                        <SelectControl
                            id="typePieceIdentite"
                            name="typePieceIdentite"
                            label="Sélectionnez votre type de pièce d'identité *"
                            options={formaterOptions(typePieceIdentite)}
                            required
                            {...register('typePieceIdentite', {
                                required: getMessageErreurChampManquant("type de pièce d'identité"),
                            })}
                            placeholder="-- Choisir une option --"
                        />
                    </div>
                </Col>
                <Col col={12} lg={6}>
                    <InputControl
                        required
                        id="numeroPieceIdentite"
                        label="Numéro de la pièce d'identité *"
                        hintText="Le nombre de caractères de la pièce d'identité est limité à 50."
                        {...register('numeroPieceIdentite', {
                            required: getMessageErreurChampManquant(
                                "numéro de la pièce d'identité",
                            ),
                        })}
                        state={formState.errors?.numeroPieceIdentite ? 'error' : null}
                        message={formState.errors?.numeroPieceIdentite?.message}
                        maxLength="50"
                    />
                </Col>
            </Row>
            <Row gutters horizontalAlign="center" verticalAlign="bottom">
                <Col col={12} lg={6}>
                    <InputControl
                        id="dateDelivrance"
                        label="Date de délivrance du document *"
                        required
                        hintText="Exemple : 01/02/1999"
                        {...register('dateDelivrance', {
                            required: getMessageErreurChampManquant(
                                'Date de délivrance du document',
                            ),
                            pattern: getPatternHookFormValidation(
                                REGEXP_DATE_AVEC_MOIS_JOUR_INCONNU,
                                '',
                                MESSAGE_DATE_INVALIDE,
                            ),
                            validate: {
                                validateDate: (value) => validateDate(value),
                            },
                        })}
                        state={formState.errors?.dateDelivrance ? 'error' : null}
                        message={formState.errors?.dateDelivrance?.message}
                        maskFonction={getDateFormatWithSlash}
                    />
                </Col>
                <Col col={12} lg={6}>
                    <InputFiles
                        name="piecesIdentite"
                        label="Ajouter ma pièce d'identité"
                        error={formState.errors ? formState.errors.piecesIdentite : null}
                        required
                        multiple
                    />
                </Col>
            </Row>
        </>
    );
};

CompteIdentiteChamps.propTypes = {
    featureFlipping: PropTypes.object,
    motifs: PropTypes.shape({
        isChasseur: PropTypes.bool,
        isLicencie: PropTypes.bool,
        isNonLicencie: PropTypes.bool,
        isDetenteurProfessionnel: PropTypes.bool,
    }),
    hasEmailPro: PropTypes.bool,
    hasTelephone: PropTypes.bool,
    titre: PropTypes.string,
};

export default CompteIdentiteChamps;
